import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { router } from '@inertiajs/react'
import { STRINGS, type StringKey } from './strings'

export type Locale = 'ja' | 'en' | 'id'

// Same key as the mockup, so a language picked there carries over on the same browser.
const STORAGE_KEY = 'gemba-assessment-locale'

interface LocaleContextValue {
  locale: Locale
  /**
   * Switch the app's language. When someone is signed in this also saves it as their preferred
   * language (the header's language menu); forms that save it themselves pass { save: false }.
   */
  setLocale: (l: Locale, options?: { save?: boolean }) => void
  t: (key: StringKey) => string
  /** Translated string with {placeholder} substitution. */
  tf: (key: StringKey, vars: Record<string, string | number>) => string
}

function format(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => (key in vars ? String(vars[key]) : match))
}

function loadInitial(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'en' || stored === 'ja' || stored === 'id' ? stored : 'en'
  } catch {
    return 'en'
  }
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(loadInitial)
  const signedIn = useRef(false)
  const lastPreferred = useRef<Locale | null>(null)

  // Each page's shared props carry the signed-in person's preferred language. Switch to it when
  // it appears or changes (i.e. at sign-in, or after it's saved), not on every page, so a
  // language picked on a form but not saved yet isn't undone by the next navigation.
  useEffect(() => router.on('navigate', (event) => {
    const props = event.detail.page.props as { current_user?: unknown; preferred_locale?: Locale | null }
    signedIn.current = Boolean(props.current_user)
    const preferred = props.preferred_locale ?? null
    if (preferred && preferred !== lastPreferred.current) setLocaleState(preferred)
    lastPreferred.current = preferred
  }), [])

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, locale) } catch { /* ignore */ }
    document.documentElement.lang = locale
  }, [locale])

  const value = useMemo<LocaleContextValue>(() => ({
    locale,
    setLocale: (l, { save = true } = {}) => {
      setLocaleState(l)
      if (save && signedIn.current) {
        router.patch('/preferred-locale', { preferred_locale: l }, { preserveState: true, preserveScroll: true, only: ['preferred_locale'] })
      }
    },
    t: (key) => STRINGS[key][locale],
    tf: (key, vars) => format(STRINGS[key][locale], vars),
  }), [locale])

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider')
  return ctx
}
